import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import TopUtilityBar from "../sections/TopUtilityBar";
import MainHeader from "../sections/MainHeader";
import Footer from "../sections/Footer";
import { collectionByHandle } from "../lib/shopify/queries";
import { clipText, useCanonical, useSeoMeta } from "../lib/seo";
import { getTaxonomyForHandle, PRICE_BOUNDS } from "../lib/filterTaxonomy";
import {
  applyFilters,
  applySort,
  buildFacets,
  readFiltersFromUrl,
  writeFiltersToUrl,
  countActive,
} from "../lib/filters";
import CollectionHero from "../components/plp/CollectionHero";
import FilterSidebar from "../components/plp/FilterSidebar";
import ActiveFilterBar from "../components/plp/ActiveFilterBar";
import SortDropdown from "../components/plp/SortDropdown";
import MobileFilterSheet from "../components/plp/MobileFilterSheet";
import EmptyState from "../components/plp/EmptyState";
import ProductCard from "../components/plp/ProductCard";
import AimTheoryCallout from "../components/plp/AimTheoryCallout";
import CategoryGuide from "../components/plp/CategoryGuide";
import Breadcrumbs from "../components/Breadcrumbs";
import { CATEGORY_GUIDES, SEO_NAMES } from "../lib/catalog/categorySeo";
import Button from "../components/loadout/Button";

const PAGE_SIZE = 24;

const titleCase = (s) => String(s).replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CollectionPage() {
  const { handle } = useParams();
  useCanonical(`/collections/${handle}`);
  const [searchParams] = useSearchParams();
  const taxonomy = useMemo(() => getTaxonomyForHandle(handle), [handle]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shopify", "collection", handle, { first: 250 }],
    queryFn: () => collectionByHandle(handle, { first: 250 }),
    staleTime: 60_000,
    retry: 1,
  });

  const products = data?.products || [];
  const source = data?.source || "empty";
  const title = data?.title || handle;
  const seoName = SEO_NAMES[handle] || (data?.title && data.title !== handle ? data.title : titleCase(handle));
  useSeoMeta(data ? {
    title: `${seoName} — Buy Online in India | Loadout`,
    description: clipText(
      (data.description && data.description.length > 60 ? `${data.description} ` : "") +
      `Shop ${products.length} authentic ${seoName.replace(/^Gaming /, "gaming ")} at Loadout — India stock & pre-orders, UPI, 18-month warranty and WhatsApp support.`,
    ),
    image: products[0]?.featuredImage?.url || products[0]?.images?.[0]?.url,
    path: `/collections/${handle}`,
  } : null);

  // ---------- Filter / sort / page state (hydrated from URL) ----------
  const initial = useMemo(
    () => readFiltersFromUrl(searchParams, taxonomy),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handle, taxonomy],
  );
  const [active, setActive] = useState(initial.active);
  const [sort, setSort] = useState(initial.sort);
  const [page, setPage] = useState(initial.page);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Pagination clicks always land the shopper at the top of the page.
  const goToPage = (n) => {
    setPage(n);
    window.scrollTo(0, 0);
  };

  // When user navigates to a different collection, reset state from URL.
  useEffect(() => {
    setActive(initial.active);
    setSort(initial.sort);
    setPage(initial.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  // Serialize state to URL after every change. Preserve the incoming path
  // prefix so /p/<id> stays /p/<id> and /collections/<handle> stays as-is.
  useEffect(() => {
    const basePath = window.location.pathname.startsWith("/p/")
      ? `/p/${handle}`
      : `/collections/${handle}`;
    writeFiltersToUrl(basePath, { active, sort, page }, taxonomy);
  }, [handle, active, sort, page, taxonomy]);

  // Listen for browser back/forward and re-hydrate.
  useEffect(() => {
    const onPop = () => {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      const state = readFiltersFromUrl(params, taxonomy);
      setActive(state.active);
      setSort(state.sort);
      setPage(state.page);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [taxonomy]);

  // ---------- Derived ----------
  const facets = useMemo(
    () => buildFacets(products, taxonomy, active),
    [products, taxonomy, active],
  );
  const filtered = useMemo(
    () => applyFilters(products, taxonomy, active),
    [products, taxonomy, active],
  );
  const sorted = useMemo(() => applySort(filtered, sort), [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE),
    [sorted, clampedPage],
  );

  const activeCount = countActive(active, taxonomy);

  // ---------- Action handlers ----------
  const toggleValue = useCallback((key, val) => {
    setActive((prev) => {
      const next = { ...prev };
      const set = next[key] instanceof Set ? new Set(next[key]) : new Set();
      if (set.has(val)) set.delete(val);
      else set.add(val);
      if (set.size === 0) delete next[key];
      else next[key] = set;
      return next;
    });
    setPage(1);
  }, []);

  const setPrice = useCallback((key, v) => {
    setActive((prev) => {
      const next = { ...prev };
      const bounded = { min: v.min, max: v.max };
      if (
        bounded.min === PRICE_BOUNDS.min &&
        bounded.max === PRICE_BOUNDS.max
      ) {
        delete next[key];
      } else {
        next[key] = bounded;
      }
      return next;
    });
    setPage(1);
  }, []);

  const clearAll = useCallback(() => {
    setActive({});
    setPage(1);
  }, []);

  const removeChip = useCallback((key, val) => {
    setActive((prev) => {
      const next = { ...prev };
      const v = next[key];
      if (v instanceof Set) {
        const s = new Set(v);
        s.delete(val);
        if (s.size === 0) delete next[key];
        else next[key] = s;
      } else if (v && typeof v === "object") {
        delete next[key];
      }
      return next;
    });
    setPage(1);
  }, []);

  // Chips ------------------------------------------------------
  const chips = useMemo(() => {
    const list = [];
    for (const [key, dim] of Object.entries(taxonomy)) {
      const v = active[key];
      if (!v) continue;
      if (dim.type === "price") {
        list.push({
          dimension: key,
          value: "range",
          label: dim.label,
          displayValue: `₹${v.min ?? PRICE_BOUNDS.min} – ₹${v.max ?? PRICE_BOUNDS.max}`,
        });
      } else if (v instanceof Set) {
        for (const val of v) {
          list.push({
            dimension: key,
            value: val,
            label: dim.label,
            displayValue: String(val).toUpperCase(),
          });
        }
      }
    }
    return list;
  }, [active, taxonomy]);

  // ---------- Body renderers ----------
  const isUnknownHandle = source === "empty" && (data?.note || "").startsWith("No mapping");

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="py-24 text-center font-mono text-[12px] uppercase tracking-[0.16em] text-[#8A8A8A]">
          LOADING…
        </div>
      );
    }
    if (isError) {
      return (
        <div className="py-24 text-center font-mono text-[12px] uppercase tracking-[0.16em] text-[#FF9F1C]">
          COULD NOT LOAD COLLECTION
        </div>
      );
    }
    if (isUnknownHandle) {
      return (
        <div className="max-w-[720px] mx-auto text-center py-24 px-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]">
            CATEGORY COMING SOON
          </div>
          <h2 className="mt-4 font-display text-white text-[32px] md:text-[40px] leading-[0.95]">
            Not on the grid yet.
          </h2>
          <p className="mt-3 font-sans text-[14px] text-[#8A8A8A]">
            {data?.note}
          </p>
          <div className="mt-6 flex justify-center">
            <Button as={Link} to="/collections/all" variant="primary">
              Browse the full Loadout →
            </Button>
          </div>
        </div>
      );
    }
    if (products.length === 0) {
      return (
        <EmptyState handle={handle} onClearFilters={clearAll} />
      );
    }

    return (
      <div className="flex gap-6 lg:gap-8">
        <FilterSidebar
          taxonomy={taxonomy}
          facets={facets}
          active={active}
          onToggleValue={toggleValue}
          onSetPrice={setPrice}
          className="hidden lg:block"
        />
        <div className="flex-1 min-w-0">
          {/* Mobile controls */}
          <div className="flex items-center gap-2 mb-4 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="flex-1 h-11 bg-[#FF9F1C] text-[#0A0A0A] font-sans text-[13px] font-bold uppercase tracking-[0.04em]"
            >
              Filters ({activeCount} active)
            </button>
            <SortDropdown value={sort} onChange={setSort} compact />
          </div>

          {/* Desktop chips + sort row */}
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            <div className="flex-1 min-w-0">
              <ActiveFilterBar chips={chips} onRemove={removeChip} onClearAll={clearAll} />
            </div>
            <div className="hidden lg:block shrink-0">
              <SortDropdown value={sort} onChange={setSort} />
            </div>
          </div>

          {sorted.length === 0 ? (
            <EmptyState handle={handle} onClearFilters={clearAll} />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {paged.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} collectionHandle={handle} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => goToPage(clampedPage - 1)}
                    disabled={clampedPage === 1}
                    className="px-3 h-10 font-mono text-[12px] uppercase text-white bg-[#141414] border border-[#1F1F1F] disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => goToPage(n)}
                      className={
                        "min-w-[40px] h-10 font-mono text-[12px] uppercase " +
                        (n === clampedPage
                          ? "bg-[#FF9F1C] text-[#0A0A0A]"
                          : "bg-[#141414] text-white border border-[#1F1F1F] hover:border-[#FF9F1C]")
                      }
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToPage(clampedPage + 1)}
                    disabled={clampedPage === totalPages}
                    className="px-3 h-10 font-mono text-[12px] uppercase text-white bg-[#141414] border border-[#1F1F1F] disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopUtilityBar />
      <MainHeader />
      {!isLoading && !isUnknownHandle && (
        <CollectionHero
          handle={handle}
          title={title}
          source={source}
          totalCount={products.length}
          visibleCount={sorted.length}
          intro={CATEGORY_GUIDES[handle]?.intro}
        />
      )}
      <main className="mx-auto max-w-[1440px] px-6 py-10 md:py-14">
        {!isLoading && !isUnknownHandle && (
          <Breadcrumbs
            className="mb-6"
            items={[{ name: "Home", path: "/" }, { name: seoName, path: `/collections/${handle}` }]}
          />
        )}
        {handle === "mice" && !isUnknownHandle && <AimTheoryCallout />}
        {renderBody()}
        {!isLoading && !isUnknownHandle && <CategoryGuide handle={handle} />}
      </main>

      <MobileFilterSheet
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        taxonomy={taxonomy}
        facets={facets}
        active={active}
        onToggleValue={toggleValue}
        onSetPrice={setPrice}
        visibleCount={sorted.length}
        onClear={clearAll}
      />

      <Footer />
    </div>
  );
}
